import { useState, useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { useOutletContext } from 'react-router-dom';
import '../../assets/style.css';
import { fetchMonitoringDashboard } from '../../services/dashboardServices';
import {
    findFirstAllowedDashboardIndex,
    isDashboardAllowed,
} from '../../helpers/dashboardAccess';

const MonitoringDashboard = () => {
    const { user = {}, isUserLoaded = false } = useOutletContext();
    const [currentDashboardIndex, setCurrentDashboardIndex] = useState(0);
    const [iframeSrc, setIframeSrc] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const iframeRef = useRef(null);
    const [dashboardSource, setDashboardSource] = useState([]);

    const loadDashboardSource = async () => {
        const data = await fetchMonitoringDashboard();
        setDashboardSource(data);
    }

    useEffect(() => {
        loadDashboardSource();
    }, []);

    // Initialize from localStorage
    useEffect(() => {
        if (!isUserLoaded) {
            setIframeSrc('');
            return;
        }

        if (dashboardSource.length === 0) {
            return;
        }

        const savedIndex = Number.parseInt(
            localStorage.getItem('monitoring_active_idx'),
            10
        );
        const savedDashboard = Number.isInteger(savedIndex)
            ? dashboardSource[savedIndex]
            : undefined;
        const activeIndex =
            savedDashboard && isDashboardAllowed(user.id, savedDashboard.id)
                ? savedIndex
                : findFirstAllowedDashboardIndex(dashboardSource, user.id);

        if (activeIndex === -1) {
            setCurrentDashboardIndex(-1);
            setIframeSrc('');
            localStorage.removeItem('monitoring_active_idx');
            return;
        }

        setCurrentDashboardIndex(activeIndex);
        setIframeSrc(dashboardSource[activeIndex].url);
        localStorage.setItem('monitoring_active_idx', String(activeIndex));
    }, [dashboardSource, isUserLoaded, user.id]);

    // Listen for sidebar-triggered storage changes
    useEffect(() => {
        const handleStorageChange = () => {
            if (!isUserLoaded || dashboardSource.length === 0) {
                return;
            }

            const savedIndex = Number.parseInt(
                localStorage.getItem('monitoring_active_idx'),
                10
            );
            const savedDashboard = Number.isInteger(savedIndex)
                ? dashboardSource[savedIndex]
                : undefined;
            const activeIndex =
                savedDashboard && isDashboardAllowed(user.id, savedDashboard.id)
                    ? savedIndex
                    : findFirstAllowedDashboardIndex(dashboardSource, user.id);

            if (activeIndex === -1) {
                setCurrentDashboardIndex(-1);
                setIframeSrc('');
                localStorage.removeItem('monitoring_active_idx');
                return;
            }

            if (activeIndex !== savedIndex) {
                localStorage.setItem(
                    'monitoring_active_idx',
                    String(activeIndex)
                );
            }

            if (activeIndex !== currentDashboardIndex) {
                setCurrentDashboardIndex(activeIndex);
                setIsLoading(true);
                setTimeout(() => {
                    setIframeSrc(dashboardSource[activeIndex].url);
                }, 300);
            }
        };
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [currentDashboardIndex, dashboardSource, isUserLoaded, user.id]);

    const handleIframeLoad = () => {
        try {
            if (iframeRef.current && iframeRef.current.contentWindow) {
                const iframeDoc = iframeRef.current.contentWindow.document;
                const style = iframeDoc.createElement('style');
                style.innerHTML = `
                    body::-webkit-scrollbar { display: none; }
                    body { -ms-overflow-style: none; scrollbar-width: none; }
                `;
                iframeDoc.head.appendChild(style);
            }
        } catch (e) {
            console.warn("Cannot inject styles into cross-origin iframe");
        }

        //loading spinner transition
        setTimeout(() => {
        setIsLoading(false);
        }, 500);
    };

    return (
        <div className="dashboard-container">
      
            {/* Main Content */}
            <main className="main-content">

                    {/* Section 2: Dashboard Display (Iframe) */}
                    <section className="iframe-section">
                    <div 
                        className={`iframe-container ${isLoading ? 'loading' : ''}`}
                    >
                        {iframeSrc && (
                        <iframe
                        ref={iframeRef}
                        src={iframeSrc}
                        className="dashboard-iframe"
                        allowFullScreen
                        onLoad={handleIframeLoad}
                        title="Dashboard Frame"
                        style={{
                            width: `${100 / 0.8}%`,
                            height: `${100 / 0.8}%`,
                            transform: `scale(${0.8})`,
                            transformOrigin: 'top left',
                        }}
                        />
                        )}
                    </div>

                    {/* Loading State */}
                    <div
                        className={`loading-overlay ${isLoading ? 'visible' : ''}`}
                    >
                        <div className="loading-content">
                        <Loader2 className="loading-spinner" />
                        <span className="loading-text">Loading dashboard...</span>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
};

export default MonitoringDashboard;
